import { CATEGORY_LABELS } from './brand';
import { EVENT_CATEGORY_ORDER, UpdateUserDetailsRequest, User, UserDetailsStats } from './types';
import { formatDayShort } from './dateFormat';
import { getPlzBundesland } from './plzBundesland';

/**
 * Freiwillige Angaben zum Konto: Radarten, Geburtsjahr (V187), Postleitzahl und Region (V191).
 * Texte und Prüfung stehen hier, damit Web und App dasselbe fragen.
 *
 * - **Direkt nach dem Anlegen** (überspringbar, Web `AccountDetailsDialog`, App
 *   `AccountDetailsSheet`): Radarten, Geburtsjahr, Postleitzahl. Nach der Region fragen wir dort
 *   nicht, sie folgt aus der Postleitzahl (`regionFromLocation`). Zwei Fragen nach dem Wohnort
 *   hintereinander wären eine zu viel (Phil, 25.10.2026).
 * - **Im Profil** stehen Standort und Region als zwei Felder nebeneinander: der Standort rechnet
 *   Entfernungen, die Region sagt, wo jemand hingehört, und lässt sich korrigieren oder fürs Ausland
 *   setzen. Beim Tippen folgt sie dem Standort, solange niemand sie selbst gewählt hat (Web).
 *
 * Die Region ist dieselbe wie beim Mitmachen in der Winterchallenge (`users.bundesland`): was hier
 * steht, ist dort vorbelegt, und wer dort eine wählt, findet sie hier wieder. Gespeichert wird sie
 * über `/auth/region`, die Postleitzahl als Standort über `/auth/location`.
 */
export const ACCOUNT_DETAILS_TITLE = 'Noch drei Fragen';
export const ACCOUNT_DETAILS_CATEGORIES_LABEL = 'Was fährst du?';
export const ACCOUNT_DETAILS_BIRTH_YEAR_LABEL = 'Geburtsjahr';
export const ACCOUNT_DETAILS_REGION_LABEL = 'Deine Region';
export const ACCOUNT_DETAILS_REGION_EMPTY = 'Keine Angabe';
export const ACCOUNT_DETAILS_POSTAL_CODE_LABEL = 'Postleitzahl';
export const ACCOUNT_DETAILS_LOCATION_LABEL = 'Wohnort';
export const ACCOUNT_DETAILS_POSTAL_CODE_PLACEHOLDER = 'z. B. 30657';

/** Leer ist keine Angabe; sonst fünf Ziffern. Der Standort wird nur in Deutschland gesucht. */
export function postalCodeError(input: string): string | null {
  const t = input.trim();
  return !t || /^\d{5}$/.test(t) ? null : 'Bitte gib eine fünfstellige Postleitzahl an.';
}

/**
 * Heimatregion aus einer Standortangabe („30657“, „30657 Hannover“). Der Standort eines Kontos
 * wird nur in Deutschland gesucht (`GeocodingService`), eine fünfstellige Zahl darin ist also eine
 * deutsche Postleitzahl. Ein reiner Ortsname ergibt nichts, dann bleibt die Region offen.
 */
export function regionFromLocation(location: string | null | undefined): string | null {
  const m = (location ?? '').match(/(^|\D)(\d{5})(\D|$)/);
  return m ? getPlzBundesland(m[2]) : null;
}

/** Die Postleitzahl aus einem gespeicherten Standort, zum Vorbelegen. */
export function postalCodeOfLocation(location: string | null | undefined): string {
  const m = (location ?? '').match(/(^|\D)(\d{5})(\D|$)/);
  return m ? m[2] : '';
}

/** Radarten zur Auswahl, in der Reihenfolge der Kategorien. „MTB" ausgeschrieben, weil hier kein Filter steht. */
export const ACCOUNT_DETAILS_CATEGORY_OPTIONS: { value: string; label: string }[] =
  EVENT_CATEGORY_ORDER.map(c => ({ value: c, label: c === 'MTB' ? 'Mountainbike' : CATEGORY_LABELS[c] }));

/** Spanne plausibler Geburtsjahre, gespiegelt in `UserService.updateDetails`. */
export function birthYearRange(now: Date = new Date()): { min: number; max: number } {
  const year = now.getFullYear();
  return { min: year - 100, max: year - 10 };
}

/**
 * Formular → Request. `error` ist gesetzt, wenn das Geburtsjahr nicht passt; leer heißt „keine
 * Angabe“ und ist kein Fehler.
 */
export function accountDetailsRequest(
  birthYearInput: string,
  categories: string[],
  now: Date = new Date(),
): { request?: UpdateUserDetailsRequest; error?: string } {
  const trimmed = birthYearInput.trim();
  let birthYear: number | null = null;
  if (trimmed) {
    const { min, max } = birthYearRange(now);
    const n = Number(trimmed);
    if (!/^\d{4}$/.test(trimmed) || n < min || n > max) {
      return { error: `Bitte gib ein Geburtsjahr zwischen ${min} und ${max} an.` };
    }
    birthYear = n;
  }
  const preferredCategories = EVENT_CATEGORY_ORDER.filter(c => categories.indexOf(c) >= 0);
  return { request: { birthYear, preferredCategories } };
}

/** Hat das Konto schon etwas angegeben? */
export const hasAccountDetails = (user: Pick<User, 'birthYear' | 'preferredCategories' | 'bundesland'> | null | undefined): boolean =>
  !!user && (user.birthYear != null || (user.preferredCategories?.length ?? 0) > 0 || !!user.bundesland);

const AGE_GROUP_LABELS: Record<string, string> = {
  U30: 'unter 30', '30': '30 bis 39', '40': '40 bis 49', '50': '50 bis 59', '60': '60 und älter',
};

export interface UserDetailsRow {
  key: string;
  label: string;
  /** „8 Konten · 40 %“ oder „weniger als 5“. */
  value: string;
  /** Anteil 0–1 für den Balken; `null` bei ausgeblendeter Gruppe. */
  share: number | null;
}

/**
 * Was die Nutzerverwaltung (Web `Users.tsx`, App `verwaltung/nutzer`) zu den freiwilligen Angaben
 * zeigt. Anteile beziehen sich auf die Konten **mit** der jeweiligen Angabe; bei den Radarten zählt
 * jede Nennung, die Summe liegt deshalb über 100 %.
 */
export function userDetailsView(d: UserDetailsStats): {
  summary: string;
  categoriesHint: string;
  agesHint: string;
  categories: UserDetailsRow[];
  ages: UserDetailsRow[];
} {
  const konten = (n: number) => (n === 1 ? '1 Konto' : `${n} Konten`);
  const row = (key: string, label: string, count: number | null, base: number): UserDetailsRow =>
    count == null
      ? { key, label, value: `weniger als ${d.minGroupSize}`, share: null }
      : { key, label, value: `${konten(count)} · ${base ? Math.round((count / base) * 100) : 0} %`, share: base ? count / base : 0 };
  const label = (key: string) => {
    const o = ACCOUNT_DETAILS_CATEGORY_OPTIONS.filter(x => x.value === key)[0];
    return o ? o.label : key;
  };
  return {
    summary: `${konten(d.withDetails)} von ${d.accounts} bestätigten haben etwas angegeben. Gefragt wird seit dem ${formatDayShort(d.since)}.`,
    categoriesHint: `${konten(d.withCategories)} mit Angabe, mehrere Radarten möglich.`,
    agesHint: `${konten(d.withBirthYear)} mit Geburtsjahr.`,
    categories: d.categories.map(g => row(g.key, label(g.key), g.count, d.withCategories)),
    ages: d.ageGroups.map(g => row(g.key, AGE_GROUP_LABELS[g.key] || g.key, g.count, d.withBirthYear)),
  };
}
